import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import styles from './RefreshControl.module.scss';

interface RefreshControlProps {
  /**
   * Re-fetches what the board shows. Resolves once the data is in, and resolves
   * to `false` when the fetch failed — the board then still holds the previous
   * rows, and saying it was updated would be a lie told to the one user who
   * cannot see that nothing changed.
   */
  onRefresh: () => void | Promise<unknown>;
  /** True while a fetch is in the air — react-query's `isFetching`. */
  isRefreshing: boolean;
  /** react-query's `dataUpdatedAt`: ms of the last successful fetch, 0 before the first one. */
  updatedAt?: number;
  /**
   * Keeps the button disabled for this long after a refresh. Default 0, no cooldown.
   *
   * For a board whose data costs something to fetch - standing orders read Nedarim's
   * GetKevaJson, capped at twenty calls an hour and shared by every fund under one
   * mosad - an always-available button invites the click that spends the budget for
   * everyone. Nobody legitimately refreshes the same list twice in half a minute, so
   * the wait is invisible in real use and only stops the drumming.
   */
  cooldownMs?: number;
}

/** How long the spoken confirmation stays in the live region before it is cleared. */
const ANNOUNCEMENT_MS = 4000;

/**
 * "רענון" בסרגל הלוח, עם שעת העדכון האחרון (HAT-1237).
 *
 * המדווח ביקש לרענן את האזור האישי בלי לצאת ממנו. טעינה מחדש של הדף היא
 * בדיוק מה שהוא רצה להימנע ממנו: סשן שלא נגעו בו מעל שעה מסתיים בטעינה
 * (ר' sessionTimeout), וכל 401 בודד מנקה את האחסון ומפנה ל-login (ר'
 * ApiClient). משיכה מחדש מתוך האפליקציה עוקפת את שניהם, והלחיצה עצמה
 * נספרת כפעילות ולכן גם מאריכה את הסשן.
 *
 * שעת העדכון אינה קישוט: הנתונים מגיעים מסנכרון מתוזמן, ולכן רענון מוצלח
 * יכול להחזיר בדיוק את אותן שורות. בלי החותמת נראה הכפתור כאילו אינו עושה
 * דבר - וזו התלונה שהובילה לדיווח מלכתחילה.
 *
 * HAT-1492: החותמת כבר לא יושבת דרך קבע ליד הכפתור, אלא בבועה שנפתחת כשעומדים
 * עליו או מגיעים אליו במקלדת, ולכמה שניות אחרי כל רענון מוצלח. כך מי שלחץ
 * רואה שהלוח נמשך גם במגע, שאין בו ריחוף. קורא מסך מקבל אותה כתיאור הכפתור.
 * גם ההמתנה והכישלון נאמרים באותה בועה ולא בשורה שמגביהה את הפס: הפס קפוא
 * מעל הלוח, וכל שינוי בגובה שלו מזיז את כל הכותרת הקפואה.
 */
const RefreshControl = ({ onRefresh, isRefreshing, updatedAt, cooldownMs = 0 }: RefreshControlProps) => {
  const { t, i18n } = useTranslation();
  const [announcement, setAnnouncement] = useState('');
  const [visibleFeedback, setVisibleFeedback] = useState('');
  const [stampShown, setStampShown] = useState(false);
  const stampId = useId();
  const cooldownId = useId();
  const [coolingDown, setCoolingDown] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const inFlightRef = useRef(false);
  const announcementTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stampTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (announcementTimer.current) clearTimeout(announcementTimer.current);
      if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
      if (stampTimer.current) clearTimeout(stampTimer.current);
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    },
    []
  );

  const scheduleFeedbackClear = useCallback(() => {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setVisibleFeedback(''), ANNOUNCEMENT_MS);
  }, []);

  const applyCooldown = useCallback(() => {
    // ההמתנה נספרת גם אחרי כישלון. משיכה שנכשלה עלתה בדיוק אותו דבר אצל
    // הספק, ולחיצות חוזרות על מסך שלא חזר הן בדיוק המקרה שמכלה את המכסה.
    if (cooldownMs <= 0) return;
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    setCoolingDown(true);
    cooldownTimer.current = setTimeout(() => setCoolingDown(false), cooldownMs);
  }, [cooldownMs]);

  const reportOutcome = useCallback(
    (succeeded: boolean) => {
      if (announcementTimer.current) clearTimeout(announcementTimer.current);
      const message = t(succeeded ? 'boardRefreshed' : 'boardRefreshFailed');
      // מנוקה אחרי כמה שניות כדי שהרענון הבא ייקרא גם הוא: מלל זהה שנשאר
      // באזור החי אינו שינוי, וקורא מסך לא מכריז עליו שוב.
      setAnnouncement(message);
      announcementTimer.current = setTimeout(() => setAnnouncement(''), ANNOUNCEMENT_MS);

      if (succeeded) {
        setVisibleFeedback('');
        if (stampTimer.current) clearTimeout(stampTimer.current);
        setStampShown(true);
        stampTimer.current = setTimeout(() => setStampShown(false), ANNOUNCEMENT_MS);
      } else {
        setVisibleFeedback(message);
        scheduleFeedbackClear();
      }

      applyCooldown();
    },
    [applyCooldown, scheduleFeedbackClear, t]
  );

  const handleClick = useCallback(async () => {
    if (inFlightRef.current || isRefreshing || coolingDown) return;

    inFlightRef.current = true;
    setInFlight(true);
    setVisibleFeedback('');

    try {
      const succeeded = (await onRefresh()) !== false;
      reportOutcome(succeeded);
    } catch {
      reportOutcome(false);
    } finally {
      inFlightRef.current = false;
      setInFlight(false);
    }
  }, [onRefresh, coolingDown, isRefreshing, reportOutcome]);

  const busy = isRefreshing || inFlight;
  /*
   * The visible label stays one short word, cooldown included: the bar above the board stays
   * put while the rows scroll (HAT-1492), so a button that grew into a sentence would push the
   * whole frozen header down and back on every refresh. The wait is said in the bubble instead.
   */
  const buttonLabel = busy ? t('refreshingBoard') : t('refreshBoard');
  const showCooldown = coolingDown && !busy;

  const updatedTime = updatedAt
    ? new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(new Date(updatedAt)).replace(/[\u200e\u200f\u061c]/g, '')
    : null;
  const describedBy = [updatedTime && stampId, showCooldown && cooldownId].filter(Boolean).join(' ') || undefined;
  const bubbleOpen = stampShown || showCooldown || Boolean(visibleFeedback);

  return (
    <div className={styles.container} data-bubble-open={bubbleOpen ? 'true' : undefined}>
      <button
        type="button"
        className={styles.button}
        onClick={handleClick}
        disabled={busy || coolingDown}
        aria-busy={busy || undefined}
        aria-disabled={coolingDown || undefined}
        aria-label={buttonLabel}
        aria-describedby={describedBy}
        data-testid="board-refresh"
        data-cooling-down={coolingDown ? 'true' : undefined}
      >
        <RefreshIcon className={`${styles.icon} ${busy ? styles.spinning : ''}`} fontSize="small" aria-hidden="true" />
        <span className={`${styles.label} body-sm-regular`}>{buttonLabel}</span>
      </button>
      {(updatedTime || showCooldown || visibleFeedback) && (
        <div className={styles.bubble}>
          {visibleFeedback && (
            <span role="alert" className={`${styles.feedbackError} body-sm-regular`} data-testid="board-refresh-feedback">
              {visibleFeedback}
            </span>
          )}
          {updatedTime && (
            <span id={stampId} className={`${styles.updatedAt} body-sm-regular`} data-testid="board-refresh-updated-at">
              {t('boardUpdatedAt')} <bdi dir="ltr">{updatedTime}</bdi>
            </span>
          )}
          {showCooldown && (
            <span id={cooldownId} className={`${styles.cooldownHint} body-sm-regular`} data-testid="board-refresh-cooldown">
              {t('refreshBoardCooldown')}
            </span>
          )}
        </div>
      )}
      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
};

export default RefreshControl;
